import {
  API_LATENCY_HOURLY_MAX_DAYS,
  API_LATENCY_SURFACES,
  tooFewForPercentiles,
  type ApiLatencyBucket,
  type ApiLatencyStats,
  type ApiLatencySummary,
  type ApiLatencySurface,
  type ApiLatencyTrend,
} from '@headline/schemas'

/**
 * How the staff latency page (`/app/api/latency`, CLI-1041) reads what the server sends: the window a range
 * means, which bucket it may use, which operation the trend charts, and how a millisecond figure and its
 * change against the baseline are written. Pure, so the page renders exactly what the tests read. The
 * figures themselves are clipbook-api's; nothing here computes a percentile.
 */

/** The ranges the page offers; the default is a week. */
export const LATENCY_RANGE_VALUES = ['24h', '7d', '30d', '90d'] as const
export type LatencyRange = (typeof LATENCY_RANGE_VALUES)[number]

const RANGE_HOURS: Record<LatencyRange, number> = {
  '24h': 24,
  '7d': 7 * 24,
  '30d': 30 * 24,
  '90d': 90 * 24,
}
const RANGE_LABELS: Record<LatencyRange, string> = {
  '24h': 'Last 24 hours',
  '7d': 'Last 7 days',
  '30d': 'Last 30 days',
  '90d': 'Last 90 days',
}
export const LATENCY_RANGES = LATENCY_RANGE_VALUES.map((value) => ({
  value,
  label: RANGE_LABELS[value],
}))

const MS_PER_HOUR = 3_600_000

/**
 * The window a range means: ending at the top of the NEXT hour (so the current hour is in it), and starting
 * that many hours before. Rounded to the hour so the query key changes once an hour, not on every render.
 */
export function latencyWindow(range: LatencyRange, now: Date): { from: string; to: string } {
  const hours = RANGE_HOURS[range]
  const end = (Math.floor(now.getTime() / MS_PER_HOUR) + 1) * MS_PER_HOUR
  return {
    from: new Date(end - hours * MS_PER_HOUR).toISOString(),
    to: new Date(end).toISOString(),
  }
}

/** Whether a range may be bucketed by the hour (clipbook-api reads hours over 31 days at most). */
export function hourlyAllowed(range: LatencyRange): boolean {
  return RANGE_HOURS[range] <= API_LATENCY_HOURLY_MAX_DAYS * 24
}

/** The bucket a range is read at: the one asked for, or day where hours are not allowed. */
export function bucketFor(range: LatencyRange, asked: ApiLatencyBucket): ApiLatencyBucket {
  return asked === 'hour' && !hourlyAllowed(range) ? 'day' : asked
}

/** A latency, written for a person: `711 ms`, `4.7 s`, `105 s`. */
export function formatMs(ms: number): string {
  if (ms < 1000) return `${Math.round(ms)} ms`
  const seconds = ms / 1000
  return seconds < 100 ? `${seconds.toFixed(1)} s` : `${Math.round(seconds)} s`
}

/** A change in latency, signed: `+3.2 s`, `−252 ms`, `±0 ms`. */
export function formatDeltaMs(ms: number): string {
  if (ms === 0) return '±0 ms'
  return `${ms > 0 ? '+' : '−'}${formatMs(Math.abs(ms))}`
}

export function formatPercent(percent: number | null): string | null {
  if (percent === null) return null
  if (percent === 0) return '±0%'
  return `${percent > 0 ? '+' : '−'}${Math.abs(percent).toFixed(Math.abs(percent) < 10 ? 1 : 0)}%`
}

const SURFACE_LABELS: Record<string, string> = { rest: 'REST', mcp: 'MCP' }
export const surfaceLabel = (surface: string) => SURFACE_LABELS[surface] ?? surface

/** A change this large, either way, is called out; anything smaller is noise at this volume. */
const NOTABLE_CHANGE_PERCENT = 20

export type DeltaTone = 'slower' | 'faster' | 'flat' | 'unreliable'

/**
 * How a p95's change reads: `unreliable` when either side served too few requests for a percentile to mean
 * anything (the page greys it), else slower or faster by at least a fifth, or flat.
 */
export function deltaTone(
  range: ApiLatencyStats | null,
  baseline: ApiLatencyStats | null,
  percent: number | null,
): DeltaTone {
  if (!range || !baseline || tooFewForPercentiles(range.requests)) return 'unreliable'
  if (tooFewForPercentiles(baseline.requests) || percent === null) return 'unreliable'
  if (percent >= NOTABLE_CHANGE_PERCENT) return 'slower'
  if (percent <= -NOTABLE_CHANGE_PERCENT) return 'faster'
  return 'flat'
}

/** A surface clipbook-api answered with, if it is one the filters know. */
export function isLatencySurface(surface: string | undefined): surface is ApiLatencySurface {
  return (API_LATENCY_SURFACES as readonly (string | undefined)[]).includes(surface)
}

export interface ChartTarget {
  surface: ApiLatencySurface
  operation: string
  /** True when nobody picked it: the busiest operation in the range. */
  busiest: boolean
}

/**
 * Which one operation the trend charts. Percentiles never add up across operations, so the chart is always
 * of one: the one picked, or the busiest in the range among those the filters allow. Null when the range
 * served nothing the filters allow and nobody picked both a surface and an operation.
 */
export function chartTarget(
  summary: ApiLatencySummary,
  filter: { surface?: string | undefined; operation?: string | undefined },
): ChartTarget | null {
  const candidates = summary.operations.filter(
    (op): op is typeof op & { surface: ApiLatencySurface } =>
      op.range !== null &&
      isLatencySurface(op.surface) &&
      (!filter.surface || op.surface === filter.surface) &&
      (!filter.operation || op.operation === filter.operation),
  )
  const busiest = candidates.toSorted(
    (a, b) => (b.range?.requests ?? 0) - (a.range?.requests ?? 0),
  )[0]
  if (busiest) {
    return { surface: busiest.surface, operation: busiest.operation, busiest: !filter.operation }
  }
  if (isLatencySurface(filter.surface) && filter.operation) {
    return { surface: filter.surface, operation: filter.operation, busiest: false }
  }
  return null
}

/** The operations the filter offers: every one the range or its baseline served, under the chosen surface. */
export function operationOptions(
  summary: ApiLatencySummary | null,
  surface: string | undefined,
  picked: string | undefined,
): string[] {
  const names = new Set(
    (summary?.operations ?? []).flatMap((op) =>
      !surface || op.surface === surface ? [op.operation] : [],
    ),
  )
  if (picked) names.add(picked)
  return [...names].toSorted((a, b) => a.localeCompare(b))
}

export interface TrendPoint {
  bucketStart: string
  requests: number
  p50Ms: number
  p95Ms: number
  p99Ms: number
}

/** The chart's rows for one operation, oldest first. */
export function trendPoints(trend: ApiLatencyTrend, target: ChartTarget): TrendPoint[] {
  return trend.points.flatMap((point) =>
    point.surface === target.surface && point.operation === target.operation
      ? [
          {
            bucketStart: point.bucketStart,
            requests: point.requests,
            p50Ms: point.p50Ms,
            p95Ms: point.p95Ms,
            p99Ms: point.p99Ms,
          },
        ]
      : [],
  )
}

/** An x-axis tick: the hour or the day, in UTC. */
export function bucketLabel(bucketStart: string, bucket: ApiLatencyBucket): string {
  const at = new Date(bucketStart)
  const day = at.toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' })
  if (bucket === 'day') return day
  const hour = at.toLocaleTimeString('en-US', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
    timeZone: 'UTC',
  })
  return `${day} ${hour}`
}

/** When a revision was first or last seen, in UTC; a dash when clipbook-api did not say. */
export function seenAt(at: string | null): string {
  if (!at) return '—'
  return new Date(at).toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
    timeZone: 'UTC',
  })
}

/** A latency read as a panel shows it: loading, not connected (the API's 503), failed, or ready. */
export type LatencyRead<T> =
  | { state: 'loading' }
  | { state: 'unconfigured' }
  | { state: 'error'; message: string }
  | { state: 'ready'; data: T }

export function latencyRead<T>(query: {
  isPending: boolean
  error: Error | null
  data: T | null | undefined
}): LatencyRead<T> {
  if (query.error) return { state: 'error', message: query.error.message }
  if (query.isPending || query.data === undefined) return { state: 'loading' }
  if (query.data === null) return { state: 'unconfigured' }
  return { state: 'ready', data: query.data }
}
